import { Thread, Message, ChatMode, ThreadSettings } from "../types";

class ThreadService {
  private readonly STORAGE_KEY = "drigonai_threads";

  // Get all threads
  getThreads(): Thread[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];

      const threads = JSON.parse(stored);
      return threads.map((thread: any) => ({
        ...thread,
        createdAt: new Date(thread.createdAt),
        updatedAt: new Date(thread.updatedAt),
        messages: thread.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        })),
      }));
    } catch (error) {
      console.error("Error loading threads:", error);
      return [];
    }
  }

  // Get a specific thread
  getThread(id: string): Thread | null {
    const threads = this.getThreads();
    return threads.find((thread) => thread.id === id) || null;
  }

  // Create a new thread
  createThread(
    name: string,
    chatMode: ChatMode,
    settings?: Partial<ThreadSettings>
  ): Thread {
    const thread: Thread = {
      id: Date.now().toString(),
      name,
      messages: [],
      chatMode,
      settings: {
        streaming: true,
        temperature: 0.7,
        selectedModel: "",
        ...settings,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const threads = this.getThreads();
    threads.unshift(thread);
    this.saveThreads(threads);

    return thread;
  }

  // Update a thread
  updateThread(id: string, updates: Partial<Thread>): Thread | null {
    const threads = this.getThreads();
    const index = threads.findIndex((thread) => thread.id === id);

    if (index === -1) return null;

    const updatedThread = {
      ...threads[index],
      ...updates,
      updatedAt: new Date(),
    };

    threads[index] = updatedThread;
    this.saveThreads(threads);

    return updatedThread;
  }

  // Add a message to a thread
  addMessage(threadId: string, message: Message): Thread | null {
    const thread = this.getThread(threadId);
    if (!thread) return null;

    const updatedThread = {
      ...thread,
      messages: [...thread.messages, message],
      updatedAt: new Date(),
    };

    return this.updateThread(threadId, updatedThread);
  }

  // Update the last assistant message (for streaming)
  updateLastAssistantMessage(threadId: string, content: string): Thread | null {
    const thread = this.getThread(threadId);
    if (!thread) return null;

    const messages = [...thread.messages];
    let lastAssistantIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i] as Message;
      if (msg.role === "assistant") {
        lastAssistantIndex = i;
        break;
      }
    }

    if (lastAssistantIndex !== -1) {
      messages[lastAssistantIndex] = {
        ...messages[lastAssistantIndex],
        content,
      };
    }

    return this.updateThread(threadId, { messages, updatedAt: new Date() });
  }

  // Delete a thread
  deleteThread(id: string): boolean {
    const threads = this.getThreads();
    const filtered = threads.filter((thread) => thread.id !== id);
    this.saveThreads(filtered);
    return threads.length !== filtered.length;
  }

  // Clear all messages in a thread
  clearThread(threadId: string): Thread | null {
    return this.updateThread(threadId, { messages: [] });
  }

  private saveThreads(threads: Thread[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(threads));
  }
}

export const threadService = new ThreadService();
